import { create } from "zustand";
import { api } from "@/lib/api";
import type { ChannelConfig } from "@ajnt/shared";

/** Routing rule for channel message delivery */
export interface RoutingRule {
  id: string;
  userId: string;
  channelType: string;
  priority: number;
  active: boolean;
}

interface ChannelState {
  configs: ChannelConfig[];
  routing: RoutingRule[];
  isLoading: boolean;
  error: string | null;
}

interface ChannelActions {
  loadConfigs(): Promise<void>;
  createConfig(
    config: Omit<ChannelConfig, "id" | "lastInteractionAt">,
  ): Promise<ChannelConfig | null>;
  updateConfig(id: string, config: Partial<ChannelConfig>): Promise<void>;
  deleteConfig(id: string): Promise<void>;
  loadRouting(): Promise<void>;
  testChannel(configId: string): Promise<{ success: boolean; error?: string }>;
}

export const useChannelStore = create<ChannelState & ChannelActions>(
  (set) => ({
    configs: [],
    routing: [],
    isLoading: false,
    error: null,

    async loadConfigs() {
      set({ isLoading: true, error: null });
      try {
        const configs = await api.get<ChannelConfig[]>(
          "/api/channels/configs",
        );
        set({ configs, isLoading: false });
      } catch (err) {
        set({
          isLoading: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to load channel configs",
        });
      }
    },

    async createConfig(config) {
      set({ isLoading: true, error: null });
      try {
        const created = await api.post<ChannelConfig>(
          "/api/channels/configs",
          config,
        );
        set((state) => ({
          configs: [...state.configs, created],
          isLoading: false,
        }));
        return created;
      } catch (err) {
        set({
          isLoading: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to create channel config",
        });
        return null;
      }
    },

    async updateConfig(id, config) {
      set({ isLoading: true, error: null });
      try {
        const updated = await api.patch<ChannelConfig>(
          `/api/channels/configs/${id}`,
          config,
        );
        set((state) => ({
          configs: state.configs.map((c) => (c.id === id ? updated : c)),
          isLoading: false,
        }));
      } catch (err) {
        set({
          isLoading: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to update channel config",
        });
      }
    },

    async deleteConfig(id) {
      set({ isLoading: true, error: null });
      try {
        await api.delete(`/api/channels/configs/${id}`);
        set((state) => ({
          configs: state.configs.filter((c) => c.id !== id),
          isLoading: false,
        }));
      } catch (err) {
        set({
          isLoading: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to delete channel config",
        });
      }
    },

    async loadRouting() {
      try {
        const routing = await api.get<RoutingRule[]>(
          "/api/dashboard/routing",
        );
        set({ routing });
      } catch (err) {
        set({
          error:
            err instanceof Error
              ? err.message
              : "Failed to load routing rules",
        });
      }
    },

    async testChannel(configId) {
      try {
        const result = await api.post<{ success: boolean; error?: string }>(
          `/api/dashboard/channels/${configId}/test`,
        );
        return result;
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error ? err.message : "Channel test failed",
        };
      }
    },
  }),
);
